import { Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "i18n-fe";
import { PROBLEM_KINDS, joinCnoList, placeByRulesProblems } from "../util/placeByRules";

// #3567 / ADR 0004 -- why a class cannot be placed by the rules, in plain words,
// with the fix one click away. Keyed off the server's `reason` and id arrays
// (util/placeByRules.js), never the server's fallback message string. Every
// i18n key below is static.
//
// `hideOpenPlacings`: on the Placings page itself there is no page to open.
// `onNavigate`: called just before a fix button navigates, so a dialog that
// lives in the page being left can close itself first.
export default function PlaceByRulesProblems({ preview, classNumber, showId, hideOpenPlacings = false, onNavigate }) {
  const { t } = useTranslation("scoreadmin");
  const navigate = useNavigate();
  const problems = placeByRulesProblems(preview);
  const num = classNumber ?? preview?.classNumber ?? "";
  const sid = showId ?? preview?.showId ?? null;
  const classId = preview?.classId;

  const go = (to) => () => {
    onNavigate?.();
    navigate(to);
  };
  const placingsTo =
    `/placings?classId=${encodeURIComponent(classId ?? "")}` + (sid ? `&showId=${encodeURIComponent(sid)}` : "");

  const cnoLabel = (r) =>
    r.competitorNumber != null
      ? t("placeByRules.cno", { cno: r.competitorNumber })
      : t("placeByRules.rideWithoutCno");
  const cnos = (rides) => joinCnoList(rides, cnoLabel, t("placeByRules.and"));

  const sentence = (p) => {
    const count = p.rides.length;
    const params = { count, cnos: cnos(p.rides), classNumber: num };
    switch (p.kind) {
      case PROBLEM_KINDS.MANUAL_RIDES:
        return t("placeByRules.problem.manualRides", params);
      case PROBLEM_KINDS.NOT_FINISHED:
        return t("placeByRules.problem.notFinished", params);
      case PROBLEM_KINDS.TIED:
        return t("placeByRules.problem.tied", params);
      case PROBLEM_KINDS.JUDGE_PLACE_MISSING:
        return t("placeByRules.problem.judgePlaceMissing", params);
      case PROBLEM_KINDS.JUDGE_PLACE_DUPLICATE:
        return t("placeByRules.problem.judgePlaceDuplicate", params);
      case PROBLEM_KINDS.ALREADY_PLACED:
        return t("placeByRules.problem.alreadyPlaced", params);
      case PROBLEM_KINDS.NO_RIDES:
        return t("placeByRules.problem.noRides", params);
      case PROBLEM_KINDS.NO_SCORES:
        return t("placeByRules.problem.noScores", params);
      case PROBLEM_KINDS.TOO_LARGE:
        return t("placeByRules.problem.tooLarge", params);
      default:
        return t("placeByRules.problem.other", params);
    }
  };

  // Tied / judge-place / already-placed / other all end at the Placings page.
  const needsPlacingsPage = problems.some(
    (p) => ![PROBLEM_KINDS.MANUAL_RIDES, PROBLEM_KINDS.NOT_FINISHED].includes(p.kind),
  );

  return (
    <Stack spacing={1.5} data-testid="place-by-rules-problems">
      {problems.map((p) => (
        <Typography key={p.kind} variant="body2" data-testid={`place-by-rules-problem-${p.kind}`}>
          {sentence(p)}
        </Typography>
      ))}
      {hideOpenPlacings && needsPlacingsPage && (
        <Typography variant="body2" color="text.secondary">
          {t("placeByRules.placeByHandHint")}
        </Typography>
      )}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {problems.some((p) => p.kind === PROBLEM_KINDS.MANUAL_RIDES) && (
          <Button variant="outlined" size="small" onClick={go("/review-queue")} data-testid="place-by-rules-open-review-queue">
            {t("placeByRules.openReviewQueue")}
          </Button>
        )}
        {problems
          .filter((p) => p.kind === PROBLEM_KINDS.NOT_FINISHED)
          .flatMap((p) => p.rides)
          .map((r) => (
            <Button
              key={r.rideId}
              variant="outlined"
              size="small"
              onClick={go(`/rides/${encodeURIComponent(r.rideId)}`)}
              data-testid={`place-by-rules-open-ride-${r.rideId}`}
            >
              {r.competitorNumber != null
                ? t("placeByRules.openRide", { cno: r.competitorNumber })
                : t("placeByRules.openRideNoCno")}
            </Button>
          ))}
        {!hideOpenPlacings && needsPlacingsPage && (
          <Button variant="outlined" size="small" onClick={go(placingsTo)} data-testid="place-by-rules-open-placings">
            {t("placeByRules.openPlacings", { classNumber: num })}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
